import {
    Container,
    Row,
    Col,
    Form,
    InputGroup,
    FloatingLabel,
    Button,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import React, { useState } from 'react';

import SEO from '@/components/common/SEO';
import { useAuth } from '@/shared/contexts/AuthContext';
import emailIcon from '@/assets/img/icons/svg/emailIcon.svg';
import lockIcon from '@/assets/img/icons/svg/lockFillIcon.svg';
import eyePasswordHide from '@/assets/img/icons/svg/eyePasswordHide.svg';
import eyePasswordShow from '@/assets/img/icons/svg/eyePasswordShow.svg';

import './SignUp.css';

const SignUp = () => {
    // State variables for form validation and submission
    const [validated, setValidated] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [errors, setErrors] = useState({});

    // axios wrapper state variables
    const { signup, logout } = useAuth();

    // Form data state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    // State to track which fields have been touched for validation
    const [touched, setTouched] = useState({
        email: false,
        password: false,
    });

    /** * Checks if the password is strong.
     * A strong password must be at least 8 characters long and contain at least one uppercase letter,
     * one lowercase letter, one digit, and one special character.
     * @param {string} password - The password to check.
     * @return {boolean} - Returns true if the password is strong, false otherwise.
     */
    const isStrongPassword = (password) => {
        // Allow common special characters: !@#$%^&*()_+-=[]{}|;:'",.<>/?`~
        const strongPasswordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{}|;:'",.<>/?`~])[A-Za-z\d!@#$%^&*()_+\-=[\]{}|;:'",.<>/?`~]{8,}$/;
        return strongPasswordRegex.test(password);
    };

    /** * Validates the form values.
     * This function checks if the email is in a valid format and if the
     * password meets the strength criteria.
     * It returns an object containing error messages for each field that fails validation.
     * @param {Object} values - The form values to validate.
     * @returns {Object} - An object containing error messages for invalid fields.
     */
    const validate = (values) => {
        const errors = {};
        const emailRegex = /^\S+@\S+\.\S+$/;

        if (!emailRegex.test(values.email)) {
            errors.email = 'Please provide a valid email.';
        }
        if (!isStrongPassword(values.password)) {
            errors.password =
                'Password must be 8+ characters with uppercase, lowercase, number, and special character.';
        }
        return errors;
    };

    /**
     * Handles the user sign-up process.
     *
     * This function validates the form data, checks if all required fields are filled and valid,
     * and ensures the user has agreed to the terms (isChecked). If validation passes, it creates
     * a new user with Firebase Authentication using the provided email and password. After successful
     * authentication, it retrieves the user's ID token and sends a POST request to the backend to
     * create the user profile with the provided first and last name.
     *
     * On success, it resets the form, displays a success message, and navigates to the login page after a delay.
     * On failure, it sets the error state with the error message.
     *
     * @async
     * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
     * @returns {Promise<void>}
     */

    const handleSignUp = async (event) => {
        event.preventDefault();
        const newErrors = validate(formData);
        setErrors(newErrors);
        setTouched({
            email: true,
            password: true,
        });

        if (Object.keys(newErrors).length > 0 || !isChecked) {
            setValidated(true);
            return;
        }

        setValidated(true);

        try {
            // create Firebase user
            await signup(formData.email, formData.password);

            // Log out immediately - user must verify email before logging in
            await logout();

            // Store ToS acceptance for profile creation step
            localStorage.setItem(
                'tosAccepted',
                JSON.stringify({
                    termsAndConditions: {
                        accepted: true,
                        dateAccepted: new Date().toISOString(),
                        versionNumber: '1.0',
                    },
                    privacyPolicy: {
                        accepted: true,
                        dateAccepted: new Date().toISOString(),
                        versionNumber: '1.0',
                    },
                })
            );

            // Show success and reset form
            setSubmitSuccess(true);
            setFormData({
                email: '',
                password: '',
            });
            setIsChecked(false);
            setErrors({});
            setValidated(false);
            setTouched({
                email: false,
                password: false,
            });

            setTimeout(() => {
                setSubmitSuccess(false);
            }, 3000);
        } catch (err) {
            setErrors({ email: err.message });
        }
    };

    return (
        <>
            <SEO
                title="Sign Up"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Sign Up', path: '/signup' },
                ]}
            />
            <div
                id="signup"
                className="page-container bg-primary d-flex justify-content-center align-items-center py-4"
            >
                <Container className="m-auto py-5">
                    <Form
                        noValidate
                        onSubmit={handleSignUp}
                        className="d-flex flex-column align-items-center bg-white gap-3 mx-auto w-100 py-5 px-3"
                        style={{ maxWidth: '720px' }}
                    >
                        {submitSuccess && (
                            <div
                                className="alert alert-success text-center w-100"
                                role="alert"
                            >
                                Account created successfully! Please check your
                                email to verify your account before logging in.
                            </div>
                        )}
                        <Row className="mb-1">
                            <h1 className="text-primary text-center">
                                Create Your Account
                            </h1>
                        </Row>

                        <Row className="flex-xl-nowrap">
                            <Form.Group as={Col} xl="6" className="mb-3">
                                <InputGroup
                                    hasValidation
                                    className="input-logo-label"
                                >
                                    <img src={emailIcon} alt="" />
                                    <FloatingLabel
                                        controlId="floatingEmail"
                                        label="Email"
                                    >
                                        <Form.Control
                                            type="email"
                                            placeholder="name@example.com"
                                            value={formData.email}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData({
                                                    ...formData,
                                                    email: val,
                                                });
                                                setErrors(
                                                    validate({
                                                        ...formData,
                                                        email: val,
                                                    })
                                                );
                                            }}
                                            onBlur={() =>
                                                setTouched({
                                                    ...touched,
                                                    email: true,
                                                })
                                            }
                                            isInvalid={
                                                touched.email && !!errors.email
                                            }
                                            isValid={
                                                touched.email &&
                                                !errors.email &&
                                                formData.email.trim() !== ''
                                            }
                                        />
                                    </FloatingLabel>
                                </InputGroup>
                                {validated && errors.email && (
                                    <div className="invalid-feedback d-block fw-semibold ps-3">
                                        {errors.email}
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group as={Col} xl="6" className="mb-3">
                                <InputGroup
                                    hasValidation
                                    className="input-logo-label position-relative"
                                >
                                    <img src={lockIcon} alt="" />
                                    <FloatingLabel
                                        controlId="floatingPassword"
                                        label="Password"
                                    >
                                        <Form.Control
                                            type={
                                                showPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            placeholder="Enter your password"
                                            value={formData.password}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData({
                                                    ...formData,
                                                    password: val,
                                                });
                                                setErrors(
                                                    validate({
                                                        ...formData,
                                                        password: val,
                                                    })
                                                );
                                            }}
                                            onBlur={() =>
                                                setTouched({
                                                    ...touched,
                                                    password: true,
                                                })
                                            }
                                            isInvalid={
                                                touched.password &&
                                                !!errors.password
                                            }
                                            isValid={
                                                touched.password &&
                                                !errors.password &&
                                                formData.password.trim() !== ''
                                            }
                                        />
                                    </FloatingLabel>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        className="position-absolute top-50 end-0 translate-middle-y me-2 bg-transparent border-0 p-0"
                                        aria-label={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                    >
                                        <img
                                            src={
                                                showPassword
                                                    ? eyePasswordShow
                                                    : eyePasswordHide
                                            }
                                            alt=""
                                            aria-hidden="true"
                                        />
                                    </button>
                                </InputGroup>
                                {validated && errors.password && (
                                    <div className="invalid-feedback d-block fw-semibold ps-3">
                                        {errors.password}
                                    </div>
                                )}
                            </Form.Group>
                        </Row>

                        <Row className="px-2 w-100">
                            <Form.Group
                                controlId="validationAgreement"
                                className="d-flex flex-column"
                            >
                                <div className="d-flex align-items-start">
                                    <Form.Check
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) =>
                                            setIsChecked(e.target.checked)
                                        }
                                        isInvalid={!isChecked && validated}
                                        className="me-2 mt-0"
                                        aria-label="I agree to the Terms and Conditions, Privacy Statement, and Acceptable Use Policy"
                                    />
                                    <p className="mb-0">
                                        By signing up, I have read and agree to
                                        the
                                        <Link
                                            to="/policies/terms"
                                            className="text-danger fw-bold px-1 text-decoration-none"
                                            target="_blank"
                                        >
                                            Terms and Conditions
                                        </Link>
                                        ,
                                        <Link
                                            to="/policies/privacy"
                                            className="text-danger fw-bold px-1 text-decoration-none"
                                            target="_blank"
                                        >
                                            Privacy Statement
                                        </Link>
                                        , and
                                        <Link
                                            to="/policies/acceptable-use"
                                            className="text-danger fw-bold px-1 text-decoration-none"
                                            target="_blank"
                                        >
                                            Acceptable Use Policy
                                        </Link>
                                        of Sustainability Hub.
                                    </p>
                                </div>
                                {validated && !isChecked && (
                                    <div className="invalid-feedback d-block fw-semibold mt-2 ps-4">
                                        You must agree to the terms, privacy
                                        statement, and acceptable use policy.
                                    </div>
                                )}
                            </Form.Group>
                        </Row>

                        <Row className="mt-4 mx-auto">
                            <Button
                                className="signup-btn btn-lg bg-primary text-white text-center fw-bold m-auto py-3"
                                type="submit"
                            >
                                Sign Up
                            </Button>
                            <Col sm={12} className="mt-3 text-center">
                                Already have an account?
                                <Link
                                    to="/login"
                                    className="text-info fw-bold ps-1"
                                    style={{
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                    }}
                                >
                                    Sign In here
                                </Link>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            </div>
        </>
    );
};

export default SignUp;
